// src/components/resources/tools/generators/CommentThread/CommentThreadGenerator.tsx

'use client'

import { useCallback, useMemo, useRef, useState, type FC } from 'react'
import { toast } from 'sonner'
import { Download } from 'lucide-react'
import { clsx } from 'clsx'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'
import {
  makeComment,
  orderThread,
  PLATFORM_EDITOR_CAPABILITIES,
  renameAuthorComments,
  toggleLikedById,
  type CommentPlatform,
  type ThreadComment,
} from './threadModel'
import { ThreadCommentEditor } from './ThreadCommentEditor'
import { InstagramComments } from './InstagramComments'
import { YouTubeComments } from './YouTubeComments'
import { TikTokComments } from './TikTokComments'
import { XComments } from './XComments'
import type { CommentPreviewProps } from './previewShared'

/** Full editable state of the comment-thread mockup. */
export interface CommentThreadState {
  platform: CommentPlatform
  theme: 'light' | 'dark'
  /** The post/video author's handle — drives "Pinned by …" and author badges. */
  postAuthor: string
  comments: ThreadComment[]
}

/**
 * Default thread: a believable product-launch comment section — a pinned
 * author comment, a customer quote with an author reply (so nested-reply
 * editing is discoverable immediately), and a verified commenter typed WITH a
 * leading `@` (exercising the normalize-first handle path in the default view).
 */
export function buildDefaultThreadState(): CommentThreadState {
  return {
    platform: 'instagram',
    theme: 'light',
    postAuthor: 'brightpath.app',
    comments: [
      makeComment({
        id: 'ct-default-1',
        username: 'brightpath.app',
        text: 'Thanks for all the launch-day support — the new reporting dashboard rolls out to every workspace this week.',
        likes: 128,
        timestamp: '6h',
        byAuthor: true,
        pinned: true,
      }),
      makeComment({
        id: 'ct-default-2',
        username: 'sofia.designs',
        text: 'The new dashboard cut our Monday reporting from two hours to fifteen minutes. Genuinely impressive.',
        likes: 214,
        timestamp: '4h',
        // Renders only on YouTube (the creator-heart badge); harmless elsewhere.
        creatorHearted: true,
        replies: [
          makeComment({
            id: 'ct-default-2-1',
            username: 'brightpath.app',
            text: 'That is exactly the workflow we built it for — thank you, Sofia!',
            likes: 32,
            timestamp: '3h',
            byAuthor: true,
          }),
        ],
      }),
      makeComment({
        id: 'ct-default-3',
        username: '@growthbymarco',
        text: 'Been testing since the beta. The export views alone are worth the upgrade.',
        likes: 87,
        timestamp: '2h',
        verified: true,
      }),
    ],
  }
}

const PLATFORM_OPTIONS: ReadonlyArray<{ id: CommentPlatform; label: string }> = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'x', label: 'X' },
]

const PREVIEW_COMPONENTS: Record<CommentPlatform, FC<CommentPreviewProps>> = {
  instagram: InstagramComments,
  youtube: YouTubeComments,
  tiktok: TikTokComments,
  x: XComments,
}

const TEXT_INPUT_CLASS =
  'rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white'

const TOGGLE_BUTTON_CLASS =
  'rounded-md px-2 py-1.5 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900'

const toggleClass = (active: boolean): string =>
  clsx(
    TOGGLE_BUTTON_CLASS,
    active
      ? 'bg-slate-900 text-white shadow-sm dark:bg-sky-500'
      : 'bg-transparent text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-800',
  )

/**
 * Props for the Comment Thread Mockup Generator. `trackingSlug` lets a future
 * platform landing variant attribute its own analytics; the umbrella route
 * omits it (same contract as the Reddit generator).
 */
export interface CommentThreadGeneratorProps {
  trackingSlug?: string
  initialPlatform?: CommentPlatform
}

/**
 * Comment Thread Mockup Generator — renders a realistic Instagram / YouTube /
 * TikTok / X comment section with fully editable nested replies, and exports a
 * retina PNG via the shared capture pipeline (deferred-preview stale gate
 * included, so the exported frame always matches the controls).
 */
export function CommentThreadGenerator({
  trackingSlug = 'comment-thread-mockup-generator',
  initialPlatform,
}: CommentThreadGeneratorProps = {}): JSX.Element {
  const [state, setState] = useState<CommentThreadState>(() => {
    const base = buildDefaultThreadState()
    return initialPlatform ? { ...base, platform: initialPlatform } : base
  })
  // Same INP posture as the Reddit generator: controls read live `state`,
  // the heavy preview renders from a deferred snapshot, and export waits for
  // the snapshot to converge (see handleExport).
  const { deferred: deferredState, isStale } = usePreviewDeferral(state)
  const previewStaleRef = useStaleRef(isStale)
  const { isExporting, runExport } = useExport()
  const previewRef = useRef<HTMLDivElement>(null)

  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])

  const trackInteraction = useCallback(
    (interaction: string, params: Record<string, string | number | boolean> = {}): void => {
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: { tool_type: 'generator', interaction_type: interaction, ...params },
      })
    },
    [trackingSlug, getDeltaEngagementMs],
  )

  const setPlatform = useCallback(
    (platform: CommentPlatform): void => {
      setState(prev => ({ ...prev, platform }))
      trackInteraction('platform_switch', { platform })
    },
    [trackInteraction],
  )

  // Like a comment by clicking its heart/thumb IN the preview — the same
  // direct-preview interaction contract as the Reddit post card's vote pill.
  const handleToggleLike = useCallback(
    (commentId: string): void => {
      setState(prev => ({ ...prev, comments: toggleLikedById(prev.comments, commentId) }))
      trackInteraction('comment_like_toggle')
    },
    [trackInteraction],
  )

  // Changing the creator handle renames the author-badged comments too:
  // `byAuthor` means "written by the post author", so the handle follows —
  // otherwise an export could pair one brand's creator header with another
  // brand's name on the author-badged comments.
  const handlePostAuthorChange = useCallback((value: string): void => {
    setState(prev => ({
      ...prev,
      postAuthor: value,
      comments: renameAuthorComments(prev.comments, value),
    }))
  }, [])

  const handleExport = useCallback(
    (): Promise<void> =>
      runExport(async beginCapture => {
        if (!previewRef.current) {
          toast.error('Preview not found. Please ensure the mockup is visible.')
          return
        }
        const stabilized = await waitForStable(() => !previewStaleRef.current)
        if (!stabilized) {
          toast.error('Preview is still updating — pause a moment, then export again.')
          return
        }
        beginCapture()
        await captureAndDownload({
          element: previewRef.current,
          format: 'png',
          filename: `${state.platform}-comment-thread-mockup-${Date.now()}`,
          trackingSlug,
          engagementMs: getTotalEngagementMs(),
          onSuccess: () => toast.success('Mockup exported successfully!'),
          onError: () => toast.error('Failed to export mockup. Please try again.'),
        })
      }),
    [runExport, state.platform, trackingSlug, getTotalEngagementMs, previewStaleRef],
  )

  const capabilities = PLATFORM_EDITOR_CAPABILITIES[state.platform]

  // Memoize the preview ELEMENT (same bail-out as the Reddit generator): an
  // urgent keystroke re-render reuses the previous element so the heavy thread
  // only re-renders on the low-priority deferred pass.
  const preview = useMemo(() => {
    const Preview = PREVIEW_COMPONENTS[deferredState.platform]
    const caps = PLATFORM_EDITOR_CAPABILITIES[deferredState.platform]
    // Pin-ordering applies only where the platform actually pins (X doesn't).
    const comments = caps.pinned ? orderThread(deferredState.comments) : deferredState.comments
    return (
      <Preview
        comments={comments}
        postAuthor={deferredState.postAuthor}
        theme={deferredState.theme}
        onToggleLike={handleToggleLike}
      />
    )
  }, [deferredState, handleToggleLike])

  return (
    <div className="flex w-full flex-col gap-6 lg:flex-row">
      <aside className="w-full space-y-6 rounded-3xl border border-slate-200 bg-white/80 p-4 shadow-lg backdrop-blur lg:w-[360px] dark:border-slate-800 dark:bg-slate-900/60">
        {/* Platform */}
        <section aria-labelledby="comment-thread-platform" className="space-y-2">
          <h3
            id="comment-thread-platform"
            className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Platform
          </h3>
          <div
            role="group"
            aria-labelledby="comment-thread-platform"
            className="grid grid-cols-4 gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-900"
          >
            {PLATFORM_OPTIONS.map(option => (
              <button
                key={option.id}
                type="button"
                onClick={() => setPlatform(option.id)}
                aria-pressed={state.platform === option.id}
                className={toggleClass(state.platform === option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </section>

        {/* Post author */}
        <section aria-labelledby="comment-thread-author" className="space-y-2">
          <h3
            id="comment-thread-author"
            className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Post Author
          </h3>
          <label className="flex flex-col gap-0.5 text-xs">
            <span className="font-medium text-slate-700 dark:text-slate-200">
              Creator handle (author-badged comments follow it)
            </span>
            <input
              type="text"
              value={state.postAuthor}
              onChange={e => handlePostAuthorChange(e.target.value)}
              placeholder="@yourbrand"
              maxLength={50}
              className={TEXT_INPUT_CLASS}
            />
          </label>
        </section>

        {/* Thread editor */}
        <section aria-labelledby="comment-thread-comments" className="space-y-2">
          <h3
            id="comment-thread-comments"
            className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Comments &amp; Replies
          </h3>
          <ThreadCommentEditor
            comments={state.comments}
            onCommentsChange={comments => setState(prev => ({ ...prev, comments }))}
            capabilities={capabilities}
            postAuthor={state.postAuthor}
            onStructureChange={interaction => trackInteraction(interaction)}
          />
        </section>

        {/* Appearance */}
        <section aria-labelledby="comment-thread-appearance" className="space-y-2">
          <h3
            id="comment-thread-appearance"
            className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Appearance
          </h3>
          <div
            role="group"
            aria-labelledby="comment-thread-appearance"
            className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-900"
          >
            {(['light', 'dark'] as const).map(theme => (
              <button
                key={theme}
                type="button"
                onClick={() => setState(prev => ({ ...prev, theme }))}
                aria-pressed={state.theme === theme}
                className={toggleClass(state.theme === theme)}
              >
                {theme === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
          </div>
        </section>

        {/* Export */}
        <section aria-labelledby="comment-thread-export" className="space-y-2">
          <h3
            id="comment-thread-export"
            className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Export
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Download the comment thread as a high-resolution PNG (captured at 2&times; retina density) — deck-ready,
            with no watermark.
          </p>
          <ExportButton
            isExporting={isExporting}
            onExport={() => void handleExport()}
            icon={<Download className="h-3 w-3" />}
            className="w-full gap-1 rounded-lg bg-slate-900 px-2 py-2 text-xs font-semibold text-white shadow-lg transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-400"
          />
        </section>
      </aside>

      <div
        className="relative flex flex-1 items-start justify-center rounded-3xl border border-white/10 p-6 shadow-2xl dark:border-slate-800"
        style={{
          background:
            deferredState.theme === 'dark' ? '#0b0b0c' : 'linear-gradient(135deg,#e8ecf3,#f6f7f8)',
        }}
      >
        {/* Capture region matched to the card width so the PNG crops tight. */}
        <div className="relative z-10 mx-auto w-full max-w-md" ref={previewRef} data-testid="comment-thread-preview">
          {preview}
        </div>
      </div>
    </div>
  )
}
